import { log } from '../log';
import type { InstrumentChangeAction, SymbolChangeAction } from '../types';
import { getTabsThatCanBeCommunicateWith } from '../utils';
import { isWorkspaceLinkingBridgeAction, publishLinkIntentFromLegacy } from '../workspaceBridge';
import { MATRIX } from './matrix';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import { notUndefined } from '@thinkalpha/common/util/notNullOrUndefined.js';
import { all, put, select, takeEvery } from 'redux-saga/effects';
import { externalAppLinkingChannelId } from 'src/features/widget/components/WidgetHeader/WidgetHeaderMenus/WidgetHeaderMenuChannelPicker';
import { getUnderlyingSymbolFromInstrument, instrumentToString, parseInstrument } from 'src/lib/instrumentUtils';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import type { RootState } from 'src/store';
import type { AppAction } from 'src/store/actions';
import { updateAlertsSymbol } from 'src/store/actions/widgets/alerts';
import { updateEventsSymbolFilter } from 'src/store/actions/widgets/events';
import { updateExecutionsInstrument } from 'src/store/actions/widgets/executions';
import type { ExternalLinkingActions } from 'src/store/actions/widgets/external';
import { updateFillingsSymbolFilter } from 'src/store/actions/widgets/filings';
import { updateAlphaLensSymbol } from 'src/store/actions/widgets/lens';
import { updateLocatesSymbol } from 'src/store/actions/widgets/locates';
import { updateLocatesV2Symbol } from 'src/store/actions/widgets/locatesV2';
import { updateMessagesInstrument } from 'src/store/actions/widgets/messages';
import { updateNewsSymbolFilter } from 'src/store/actions/widgets/news';
import { updateOrderBlotterInstrument } from 'src/store/actions/widgets/orderBlotter';
import { updateOrdersInstrument } from 'src/store/actions/widgets/orders';
import { updatePositionsInstrument } from 'src/store/actions/widgets/positions';
import { updateTradeProSymbol, updateTradeProInstrument } from 'src/store/actions/widgets/tradePro';
import { updateTradingViewChartSymbol } from 'src/store/actions/widgets/tradingViewChart';
import type { TabState } from 'src/store/reducers/tab';
import type { WidgetViewModel, WidgetTabViewModel } from 'src/store/types';

/** @deprecated move to instrumentLinkingSagas */
export function* symbolLinkingSagas() {
    const actionsForSymbolLinking: AppAction['type'][] = [
        'userDoubleClickedSymbolFromTable',
        'userSelectedNewAlphaLensSymbol',
        'changeTradingViewChartSymbol',
        'userSelectedNewTradeProSymbol',
        'userSelectedNewLocatesSymbol',
        'userDoubleClickedPositionFromTable',
        'userDoubleClickedOrderFromBlotter',
        'userSetNewsSymbolFilter',
        'userSetEventsSymbolFilter',
    ];

    yield all([
        takeEvery(actionsForSymbolLinking, handleSymbolChange),
        takeEvery('externalLinkingSymbolFetched', externalLinkingSymbolFetched),
    ]);
}

export function* instrumentLinkingSagas() {
    const actionsForInstrumentLinking: AppAction['type'][] = [
        'updateTradeProInstrument',
        'updateOrdersInstrument',
        'updateMessagesInstrument',
        'updateExecutionsInstrument',
        'updatePositionsInstrument',
        'updateOrderBlotterInstrument',
        'workspaceLinkingInstrumentSelected',
    ];

    yield all([takeEvery(actionsForInstrumentLinking, handleInstrumentChange)]);
}

/** @deprecated move to instrumentLinkingSagas */
export function* handleSymbolChange(action: SymbolChangeAction & AppAction) {
    if (!action.symbol) return;

    if (!isWorkspaceLinkingBridgeAction(action)) {
        yield* publishLinkIntentFromLegacy({
            type: 'instrument-selected',
            fromTabId: action.tabId,
            details: { instrument: action.symbol },
        });
    }

    const widgetsThatShouldReceiveUpdates = yield* getTabsThatCanBeCommunicateWith(action.tabId, MATRIX);
    if (!widgetsThatShouldReceiveUpdates.length) return;

    const widgetUpdates = getWidgetUpdateActions(widgetsThatShouldReceiveUpdates, action.symbol);
    if (widgetUpdates.length === 0) return;

    yield all(widgetUpdates.map((x) => put(x)));
}

/** @deprecated move to instrumentLinkingSagas */
function* externalLinkingSymbolFetched(action: ExternalLinkingActions) {
    const allWidgetsInWorkspace: TabState['byId'] = yield select((state: RootState) => state.tab.byId);
    const allTabsInDashboard = Object.values(allWidgetsInWorkspace);

    const tabsWithExternalChannel = allTabsInDashboard.filter((tab) => tab.channelId === externalAppLinkingChannelId);
    if (!tabsWithExternalChannel.length) return;

    const widgetUpdates = getWidgetUpdateActions(tabsWithExternalChannel, action.symbol);
    if (widgetUpdates.length === 0) return;

    yield all(widgetUpdates.map((x) => put(x)));
}

/** @deprecated move to instrumentLinkingSagas */
function getWidgetUpdateActions(
    widgetsThatShouldReceiveUpdates: WidgetTabViewModel<WidgetViewModel>[],
    symbol: string,
) {
    /**
     * This is a temporary block of code, to support widgets that have fully migrated to Instrument.
     * But since we have some widgets that only support symbol we need to pass an Instrument to the updated widgets
     */
    const instrument = parseInstrument(symbol);

    return widgetsThatShouldReceiveUpdates
        .map((x) => {
            const type = x.widget.type;
            switch (type) {
                case 'results':
                    // TODO what does it mean to update the symbol for a results widget?
                    return undefined;
                case 'order-blotter':
                    return updateOrderBlotterInstrument(x.id, instrument, true);
                case 'trade-pro':
                case 'trade-pro-multi':
                    return updateTradeProSymbol(x.id, symbol);
                case 'messages':
                    return updateMessagesInstrument(x.id, instrument, true);
                case 'positions':
                    return updatePositionsInstrument(x.id, instrument, true);
                case 'executions':
                    return updateExecutionsInstrument(x.id, instrument, true);
                case 'orders':
                    return updateOrdersInstrument(x.id, instrument, true);
                case 'locates':
                    return updateLocatesSymbol(x.id, symbol);
                case 'lab':
                    return updateLocatesV2Symbol(x.id, symbol);
                case 'tradingview-chart':
                    return updateTradingViewChartSymbol(x.id, symbol);
                case 'alpha-lens':
                    return updateAlphaLensSymbol(x.id, symbol);
                case 'searchalpha':
                    // TODO what does it mean to update the symbol for a results area widget?
                    return undefined;
                case 'screener':
                    // TODO what does it mean to update the symbol for a results area widget?
                    return undefined;
                case 'scanner':
                    // TODO what does it mean to update the symbol for a results area widget?
                    return undefined;
                case 'news':
                    return updateNewsSymbolFilter(x.id, symbol);
                case 'events':
                    return updateEventsSymbolFilter(x.id, symbol);
                case 'filings':
                    return updateFillingsSymbolFilter(x.id, symbol);
                case 'alerts':
                    return updateAlertsSymbol(x.id, symbol);
                case 'accounts':
                case 'manual-review':
                case 'table':
                case 'watchlist':
                case 'basket-trading':
                case 'alpha-eye':
                case 'notepad':
                case 'platforms':
                case 'symbols':
                case 'traders':
                case 'backtest':
                    if (TRACE) {
                        log.trace({ message: 'symbol change for widget not handled', widget: x.widget, symbol });
                    }
                    return undefined;
                default:
                    throw new UnreachableCaseError(type);
            }
        })
        .filter(notUndefined);
}

export function* handleInstrumentChange(action: InstrumentChangeAction & AppAction) {
    if (!action.instrument) return;

    if (!isWorkspaceLinkingBridgeAction(action)) {
        yield* publishLinkIntentFromLegacy({
            type: 'instrument-selected',
            fromTabId: action.tabId,
            details: { instrument: instrumentToString(action.instrument) },
        });
    }

    const widgetsThatShouldReceiveUpdates = yield* getTabsThatCanBeCommunicateWith(action.tabId, MATRIX);
    if (!widgetsThatShouldReceiveUpdates.length) return;

    const widgetUpdates = getWidgetUpdateActionsForInstrument(widgetsThatShouldReceiveUpdates, action.instrument);
    if (widgetUpdates.length === 0) return;

    yield all(widgetUpdates.map((x) => put(x)));
}

function getWidgetUpdateActionsForInstrument(
    widgetsThatShouldReceiveUpdates: WidgetTabViewModel<WidgetViewModel>[],
    instrument: Instrument,
) {
    /**
     * This is a temporary block of code, to support widgets that have fully migrated to Instrument.
     * since we have some widgets that only support symbol we need to pass the
     * underlying equity symbol to the those widgets
     */
    const underlyingSymbol = getUnderlyingSymbolFromInstrument(instrument);

    return widgetsThatShouldReceiveUpdates
        .map((x) => {
            const type = x.widget.type;
            switch (type) {
                case 'results':
                    // TODO what does it mean to update the symbol for a results widget?
                    return undefined;
                case 'order-blotter':
                    return updateOrderBlotterInstrument(x.id, instrument, true);
                case 'trade-pro':
                case 'trade-pro-multi':
                    return updateTradeProInstrument(x.id, instrument, true);
                case 'messages':
                    return updateMessagesInstrument(x.id, instrument, true);
                case 'positions':
                    return updatePositionsInstrument(x.id, instrument, true);
                case 'executions':
                    return updateExecutionsInstrument(x.id, instrument, true);
                case 'orders':
                    return updateOrdersInstrument(x.id, instrument, true);
                case 'locates':
                    return updateLocatesSymbol(x.id, underlyingSymbol);
                case 'lab':
                    return updateLocatesV2Symbol(x.id, underlyingSymbol);
                case 'tradingview-chart':
                    return updateTradingViewChartSymbol(x.id, underlyingSymbol);
                case 'alpha-lens':
                    return updateAlphaLensSymbol(x.id, underlyingSymbol);
                case 'searchalpha':
                    // TODO what does it mean to update the symbol for a results area widget?
                    return undefined;
                case 'screener':
                    // TODO what does it mean to update the symbol for a results area widget?
                    return undefined;
                case 'scanner':
                    // TODO what does it mean to update the symbol for a results area widget?
                    return undefined;
                case 'news':
                    return updateNewsSymbolFilter(x.id, underlyingSymbol);
                case 'events':
                    return updateEventsSymbolFilter(x.id, underlyingSymbol);
                case 'filings':
                    return updateFillingsSymbolFilter(x.id, underlyingSymbol);
                case 'alerts':
                    return updateAlertsSymbol(x.id, underlyingSymbol);
                case 'accounts':
                case 'manual-review':
                case 'table':
                case 'watchlist':
                case 'basket-trading':
                case 'alpha-eye':
                case 'notepad':
                case 'platforms':
                case 'symbols':
                case 'traders':
                case 'backtest':
                    if (TRACE) {
                        log.trace({
                            message: 'instrument change for widget not handled',
                            widget: x.widget,
                            instrument,
                        });
                    }
                    return undefined;
                default:
                    throw new UnreachableCaseError(type);
            }
        })
        .filter(notUndefined);
}
